import { createRoot } from "react-dom/client";
import React from "react";
import { useConfig } from "../../../../../../context/ConfigContext";
import ConfigContext from "../../../../../../context/ConfigContext";
import PrintableInvoice from "./PrintableInvoice";
import { normalizeInvoiceForPrint } from "./normalizeInvoiceForPrint";
import { getDefaultInvoiceLayout } from "./invoiceLayoutDefaults";

// Renders PrintableInvoice into a hidden off-screen container and triggers window.print().
// Replaces the ad-hoc string-built-HTML print functions across the old invoice print files.
export default function useInvoicePrint() {
  const configContextValue = useConfig() || {};
  const { invoiceLayoutSettings, storeInfo } = configContextValue;

  const printInvoice = (invoiceType, rawInvoiceData, onDone) => {
    const layout =
      (invoiceLayoutSettings && invoiceLayoutSettings[invoiceType]) ||
      getDefaultInvoiceLayout(invoiceType);
    const invoice = normalizeInvoiceForPrint(rawInvoiceData, invoiceType);

    // The mount point is a real, unclipped element positioned off-screen for
    // normal browsing. It must NOT be zero-sized/overflow:hidden while
    // clipped, though -- a position:fixed 0x0 overflow:hidden ancestor also
    // clips its own descendants during print (regardless of the descendant's
    // own position:absolute), which previously produced a near-blank page
    // with only the first couple of lines visible. The injected stylesheet
    // below resets this container to normal, unclipped flow specifically
    // for @media print, while keeping it invisible/off-screen on screen.
    let printStyle = document.getElementById("__invoice_print_style");
    if (!printStyle) {
      printStyle = document.createElement("style");
      printStyle.id = "__invoice_print_style";
      printStyle.textContent = `
        .__invoice_print_root {
          position: fixed;
          left: -99999px;
          top: 0;
          width: 0;
          height: 0;
          overflow: hidden;
        }
        @media print {
          .__invoice_print_root {
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: auto !important;
            height: auto !important;
            overflow: visible !important;
          }
        }
      `;
      document.head.appendChild(printStyle);
    }

    // Defensive cleanup: an earlier print (e.g. the browser's own print
    // dialog cancelled without firing "afterprint", or a rapid double click)
    // can leave a stale mount behind. Since a CSS class/id selector matches
    // EVERY element sharing it, a leftover mount would also be revealed
    // during print alongside the new one -- producing a duplicated invoice
    // and a spurious extra page. Always clear stale mounts before adding a
    // new one so at most one is ever present.
    document.querySelectorAll(".__invoice_print_root").forEach((stale) => {
      if (stale.parentNode) stale.parentNode.removeChild(stale);
    });

    const container = document.createElement("div");
    container.className = "__invoice_print_root";
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = () => {
      window.removeEventListener("afterprint", cleanup);
      try {
        root.unmount();
      } catch (e) {
        // ignore
      }
      if (document.body.contains(container)) document.body.removeChild(container);
      if (onDone) onDone();
    };

    // The print root is a *separate* React tree mounted directly on
    // document.body via createRoot(), so it sits outside the app's own
    // component tree and has no ConfigContext.Provider ancestor. Templates
    // inside it call useFormattingUtils()/useConfig(), which would otherwise
    // get `undefined` and crash trying to destructure `config`. Re-provide
    // the same context value here so formatting/currency/date settings work
    // inside the disconnected print tree too.
    root.render(
      React.createElement(
        ConfigContext.Provider,
        { value: configContextValue },
        React.createElement(PrintableInvoice, {
          layout,
          invoice,
          storeInfo: storeInfo || {},
        })
      )
    );

    window.addEventListener("afterprint", cleanup);

    setTimeout(() => {
      window.print();
      // Fallback cleanup in case afterprint doesn't fire (some browsers/printer dialogs).
      setTimeout(cleanup, 2000);
    }, 150);
  };

  return { printInvoice };
}
